import { useEffect, useRef, useState } from 'react';
import {
  BANNER_JITTER_MAX_MS,
  BUILD_N,
  BUILD_SHA,
  VERSION_POLL_MS,
  bannerDelayMs,
  isStale,
  parseVersion,
} from './version-check';
import { rumAction, logCall } from './rum';

// Polls the deploy's published version and reports whether to show the refresh banner
// (see version-check.ts for why a poll and not a socket frame).
//
// Detection and the banner are separate moments. Detection reports once, immediately,
// because it is the only signal that publishing works at all; the banner then waits out
// the tier's delay, which for `routine` is a day.
//
// Polling continues through that wait rather than latching on detection. A `wanted` deploy
// during a routine tab's day-long wait has to be able to pull the banner forward — a tab
// that stopped looking would sit silent for the rest of the day, which is exactly the case
// the label exists for.
export function useStaleBundle(): { showBanner: boolean } {
  // When the banner is due, in epoch ms.
  const [bannerAt, setBannerAt] = useState<number | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  // Drawn once per tab, not per check: a jitter re-rolled on every poll would let a tab
  // creep toward showing the banner immediately, and the spread is the whole point.
  const jitterRef = useRef(Math.random() * BANNER_JITTER_MAX_MS);
  // Survives the effect re-running, which the local guards below cannot: without it, every
  // re-run would report the same staleness again.
  const reportedRef = useRef(false);

  useEffect(() => {
    // No sha inlined => dev/local/test. Nothing to compare against, so never fetch. Once
    // the banner is up there is nothing left to learn.
    if (BUILD_SHA == null || showBanner) return;
    const controller = new AbortController();
    let cancelled = false;
    let inFlight = false;

    const check = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      try {
        // ABSOLUTE path: assets are served from a sha-scoped base (`/v/<sha>/`), so a
        // relative fetch would ask the immutable prefix for the one file that has to be
        // current. `no-store` on top of the object's own no-cache headers.
        const res = await fetch('/version.json', { cache: 'no-store', signal: controller.signal });
        if (!res.ok) return;
        const published = parseVersion(await res.json());
        if (cancelled || !isStale({ sha: BUILD_SHA, n: BUILD_N }, published)) return;
        // Only ever earlier: a later deploy can upgrade this tab's tier from `routine` to
        // `wanted` and pull the banner forward, but nothing may push it back and keep a
        // tab that has already waited most of a day waiting again.
        const due = Date.now() + bannerDelayMs(published.tier, jitterRef.current);
        setBannerAt((prev) => (prev == null ? due : Math.min(prev, due)));
        if (reportedRef.current) return;
        reportedRef.current = true;
        // The action marks the session replay; the log is the alertable half — a RUM
        // action cannot be queried from a monitor at all (see rum.ts). Without the log,
        // a publish that silently breaks (bad JSON, wrong bucket) is indistinguishable
        // from "no deploys happened", because every failure path here is swallowed.
        // Emitted on detection, not on the banner, so a quiet tier still reports.
        const context = {
          running_sha: BUILD_SHA,
          published_sha: published.sha,
          running_n: BUILD_N,
          published_n: published.n,
          tier: published.tier,
        };
        rumAction('cockpit.bundle.stale', context);
        logCall('bundle_stale', { event: 'cockpit_bundle_stale', ...context });
      } catch {
        // Offline, aborted, or the SPA fallback answered with HTML. No news.
      } finally {
        inFlight = false;
      }
    };

    void check();
    const id = setInterval(() => void check(), VERSION_POLL_MS);
    // A forgotten tab's timers are throttled to roughly once a minute, so the poll alone
    // can lag badly. Checking as it becomes visible means the SDR sees the banner the
    // moment they look at the tab rather than up to a poll later.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [showBanner]);

  // Separate from the poll: that effect early-returns once detection latches, and its
  // cleanup would clear this timer the instant it was armed.
  useEffect(() => {
    if (bannerAt == null || showBanner) return;
    // A `routine` wait is ~24h, and one timer that long is not a clock — background
    // throttling and machine sleep make it arbitrarily late, on exactly the forgotten tab
    // this delay exists to serve. Re-check whenever the tab is looked at.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = (): void => {
      const remaining = bannerAt - Date.now();
      if (remaining <= 0) {
        setShowBanner(true);
        return;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(arm, remaining);
    };
    arm();
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') arm();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [bannerAt, showBanner]);

  return { showBanner };
}
